import { useEffect, useState } from "react";
import { Pulse, ArrowSquareOut } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";

const TACTICAL_BG =
  "https://static.prod-images.emergentagent.com/jobs/784dbb52-c333-452b-9dc0-e74e42a3174e/images/24c13a4e20bfe1717594dc8307e92bd939207bf3a33b8fae454905715051504d.png";

const formatDate = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("da-DK", { day: "2-digit", month: "short" });
};

const ResultPill = ({ letter }) => {
  const map = {
    V: "bg-brand text-black border-brand",
    U: "bg-yellow-500/80 text-black border-yellow-500",
    T: "bg-red-500/80 text-black border-red-500",
  };
  return (
    <span
      className={`grid h-7 w-7 place-items-center border font-heading text-xs ${map[letter] || "bg-neutral-800 text-white border-neutral-700"}`}
    >
      {letter}
    </span>
  );
};

const Row = ({ m }) => {
  const isResult = m.type === "resultat";
  const homeWin = isResult && m.result_home > m.result_away;
  const awayWin = isResult && m.result_away > m.result_home;
  return (
    <div
      data-testid={`match-row-${m.id}`}
      className="grid grid-cols-[72px_1fr_auto_1fr] items-center gap-3 border-b border-neutral-900 px-4 py-5 transition-colors hover:bg-white/[0.02] sm:grid-cols-[96px_1fr_auto_1fr_120px] sm:px-6"
    >
      <div className="flex flex-col">
        <span className="font-heading text-2xl leading-none text-white">
          {formatDate(m.date).split(".")[0]}
        </span>
        <span className="mono-label mt-1 text-neutral-500">
          {formatDate(m.date).split(".").slice(1).join(".").trim()}
        </span>
      </div>

      <div className="text-right">
        <div
          className={`font-heading text-lg uppercase tracking-tight sm:text-xl ${homeWin ? "text-brand" : "text-white"}`}
        >
          {m.home}
        </div>
      </div>

      <div className="flex min-w-[96px] flex-col items-center">
        {isResult ? (
          <div className="font-heading text-3xl tracking-tight text-white">
            <span className={homeWin ? "text-brand" : ""}>{m.result_home}</span>
            <span className="mx-2 text-neutral-500">–</span>
            <span className={awayWin ? "text-brand" : ""}>{m.result_away}</span>
          </div>
        ) : (
          <div className="mono-label border border-neutral-700 px-3 py-1 text-neutral-300">
            {m.time}
          </div>
        )}
        <span className="mono-label mt-1 text-neutral-500">
          {isResult ? "Slut" : "Spark"}
        </span>
      </div>

      <div>
        <div
          className={`font-heading text-lg uppercase tracking-tight sm:text-xl ${awayWin ? "text-brand" : "text-white"}`}
        >
          {m.away}
        </div>
      </div>

      <div className="col-span-4 mt-1 flex flex-wrap gap-3 sm:col-span-1 sm:mt-0 sm:justify-end">
        <span className="mono-label text-neutral-500">{m.competition}</span>
      </div>
    </div>
  );
};

const FormPill = ({ item }) => {
  const map = {
    V: "border-brand bg-brand text-black",
    U: "border-yellow-500 bg-yellow-500/80 text-black",
    T: "border-red-500 bg-red-500/80 text-black",
  };
  const isHome = item.home.toLowerCase().includes("taastrup");
  const us = isHome ? item.home_score : item.away_score;
  const them = isHome ? item.away_score : item.home_score;
  const opponent = isHome ? item.away : item.home;
  return (
    <div className="flex items-center gap-3 border border-neutral-800 bg-black/60 px-3 py-2">
      <span
        className={`grid h-7 w-7 place-items-center border font-heading text-xs ${map[item.result] || ""}`}
      >
        {item.result}
      </span>
      <div className="flex-1">
        <div className="font-body text-sm text-white">{opponent}</div>
        <div className="mono-label text-neutral-500">
          {isHome ? "Hjemme" : "Ude"}
        </div>
      </div>
      <div className="font-heading text-xl text-white">
        {us}<span className="text-neutral-500">-</span>{them}
      </div>
    </div>
  );
};

export const Kampprogram = () => {
  const { data: adminMatches } = useFetch("/matches");
  const [dbu, setDbu] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [dbuLoading, setDbuLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/dbu/hs1").then((r) => setDbu(r.data)).catch(() => setDbu(null)),
      api
        .get("/dbu/hs1/schedule")
        .then((r) => setSchedule(r.data))
        .catch(() => setSchedule(null)),
    ]).finally(() => setDbuLoading(false));
  }, []);

  // DBU schedule overrides admin-curated when available
  const matches =
    (schedule?.matches && schedule.matches.length > 0
      ? schedule.matches
      : adminMatches) || [];
  const upcoming = matches.filter((m) => m.type === "kamp");
  const results = matches.filter((m) => m.type === "resultat");

  const tfcStanding = dbu?.standings?.find((s) => s.is_taastrup);
  const usingDbu = (schedule?.matches?.length || 0) > 0;

  return (
    <section
      id="kampe"
      data-testid="kampprogram-section"
      className="relative border-t border-neutral-900 py-24 lg:py-32"
    >
      <img
        src={TACTICAL_BG}
        aria-hidden="true"
        alt=""
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.12]"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-transparent to-[#0A0A0A]" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="section-eyebrow">03 · Kampprogram</span>
            <h2 className="font-heading text-5xl uppercase leading-none tracking-tight text-white sm:text-6xl lg:text-7xl">
              Kampe & Resultater
            </h2>
            <p className="mt-3 font-body text-neutral-400">
              Live data fra DBU for Herresenior 1 ·{" "}
              <span className="text-brand">{dbu?.league || "Sjællandsserien"}</span>
            </p>
          </div>
          {dbu && (
            <div
              data-testid="dbu-live-badge"
              className="inline-flex items-center gap-2 border border-brand/40 bg-brand/10 px-3 py-2 mono-label text-brand"
            >
              <Pulse size={14} weight="fill" className="animate-pulse" />
              Live fra DBU
            </div>
          )}
        </div>

        {/* DBU Live data: standing + form */}
        {!dbuLoading && dbu && (dbu.form?.length > 0 || dbu.standings?.length > 0) && (
          <div
            data-testid="dbu-live-block"
            className="mt-10 grid gap-6 lg:grid-cols-2"
          >
            {/* Form */}
            {dbu.form?.length > 0 && (
              <div className="hairline bg-[#0a0a0a]/90">
                <div className="flex items-center justify-between border-b border-neutral-800 bg-black px-5 py-3">
                  <span className="mono-label text-brand">Seneste 5 kampe</span>
                  <a
                    href={dbu.source}
                    target="_blank"
                    rel="noreferrer"
                    className="mono-label inline-flex items-center gap-1 text-neutral-500 hover:text-brand"
                  >
                    Kilde <ArrowSquareOut size={12} />
                  </a>
                </div>
                <div className="grid gap-2 p-3">
                  {dbu.form.map((f, i) => (
                    <FormPill key={i} item={f} />
                  ))}
                </div>
              </div>
            )}

            {/* Standing snippet */}
            {tfcStanding && (
              <div className="hairline bg-[#0a0a0a]/90">
                <div className="flex items-center justify-between border-b border-neutral-800 bg-black px-5 py-3">
                  <span className="mono-label text-brand">Stilling</span>
                  <span className="mono-label text-neutral-500">
                    {dbu.league}
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-3 p-5">
                  <div>
                    <div className="mono-label text-neutral-500">Plads</div>
                    <div className="mt-1 font-heading text-4xl text-brand">
                      #{tfcStanding.position}
                    </div>
                  </div>
                  <div>
                    <div className="mono-label text-neutral-500">Point</div>
                    <div className="mt-1 font-heading text-4xl text-white">
                      {tfcStanding.points}
                    </div>
                  </div>
                  <div>
                    <div className="mono-label text-neutral-500">Kampe</div>
                    <div className="mt-1 font-heading text-4xl text-white">
                      {tfcStanding.played}
                    </div>
                  </div>
                  <div>
                    <div className="mono-label text-neutral-500">V-U-T</div>
                    <div className="mt-1 font-heading text-2xl text-white">
                      {tfcStanding.wins}-{tfcStanding.draws}-{tfcStanding.losses}
                    </div>
                  </div>
                </div>
                {dbu.stats?.goals && (
                  <div className="grid grid-cols-3 gap-3 border-t border-neutral-900 p-5">
                    <div>
                      <div className="mono-label text-neutral-500">Mål</div>
                      <div className="mt-1 font-mono text-lg text-white">
                        {dbu.stats.goals}
                      </div>
                    </div>
                    <div>
                      <div className="mono-label text-neutral-500">Snitalder</div>
                      <div className="mt-1 font-mono text-lg text-white">
                        {dbu.stats.avg_age} år
                      </div>
                    </div>
                    <div>
                      <div className="mono-label text-neutral-500">Gule kort</div>
                      <div className="mt-1 font-mono text-lg text-white">
                        {dbu.stats.yellow_cards}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Admin curated upcoming + results */}
        <div className="mt-10 grid gap-10 lg:grid-cols-2">
          <div
            data-testid="matches-upcoming"
            className="hairline bg-[#0a0a0a]/90"
          >
            <div className="flex items-center justify-between border-b border-neutral-800 bg-black px-5 py-3">
              <span className="mono-label text-brand">Kommende</span>
              <span className="mono-label text-neutral-500">
                {upcoming.length} kampe {usingDbu && "· DBU"}
              </span>
            </div>
            <div>
              {upcoming.slice(0, 6).map((m) => (
                <Row key={m.id} m={m} />
              ))}
              {upcoming.length === 0 && (
                <p className="p-6 text-center text-sm text-neutral-500">
                  Ingen kommende kampe i DBU's program lige nu.
                </p>
              )}
            </div>
          </div>

          <div
            data-testid="matches-results"
            className="hairline bg-[#0a0a0a]/90"
          >
            <div className="flex items-center justify-between border-b border-neutral-800 bg-black px-5 py-3">
              <span className="mono-label text-brand">Resultater</span>
              <span className="mono-label text-neutral-500">
                {results.length} kampe {usingDbu && "· DBU"}
              </span>
            </div>
            <div>
              {results.slice(-6).reverse().map((m) => (
                <Row key={m.id} m={m} />
              ))}
              {results.length === 0 && (
                <p className="p-6 text-center text-sm text-neutral-500">
                  Resultater opdateres efter kampe.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
